import React, { useState, useEffect } from "react"
import _isEmpty from "lodash/isEmpty"
import _startCase from "lodash/startCase"
import { withOrientationChange } from "react-device-detect"

import { apim } from "@/constants/api"
import {
  validateField,
  validatePhone,
  getSiteWideModal,
  sanitizeObject,
} from "@/utils/helper"
import CONSTANTS, { CONTACT_US_VALIDATE_RECAPTCHA_ENDPOINT } from "@/constants"

import Button from "@/components/core/Button/Button"
import FormTabs from "@/components/ContactUs/v1/FormTabs"
import Dropdown from "@/components/core/SelectDropdown/SelectDropdown"
import UploadImage from "@/components/ContactUs/v1/UploadImage"
import ContactInfo from "@/components/ContactUs/v1/Form/ContactInfo"
import WithProfessional from "@/components/ContactUs/v1/Form/WithProfessional"
import ContactUsConfirmation from "@/components/ContactUs/v1/ContactUsConfirmation"
import styles from "@/components/core/TextArea/index.module.scss"
import ReCaptcha, {
  validateRecaptcha,
} from "@/components/core/ReCaptcha/ReCaptcha"

import {
  getAnalyticsData,
  addFormAnalyticsData,
  getFooterEventInfo,
  getRecaptchaAnalytics,
} from "@/components/ContactUs/v1/ContactUsView/contactUsViewAnalytics"

const ContactUs = props => {
  const [error, setError] = useState({})
  const [userData, setUserData] = useState({
    fname: "",
    lname: "",
    email: "",
    cemail: "",
    zipcode: "",
    country: {},
    phone: "",
    company: "",
    market: "Residential",
    professionalName: "",
  })
  const [homeOwnerData, setHomeOwnerData] = useState({
    fname: "",
    lname: "",
    email: "",
    cemail: "",
    zipcode: "",
    country: {},
    phone: "",
    company: "",
    professionalName: "",
  })
  const [professionalData, setProfessionalData] = useState({
    fname: "",
    lname: "",
    email: "",
    cemail: "",
    zipcode: "",
    country: {},
    phone: "",
    company: "",
    market: "Residential",
  })
  const {
    CREATE_ACCOUNT_TYPE: { HOME_OWNER = "", TRADE_ACCOUNT = "" },
  } = CONSTANTS
  const HOME_OWNER_TAB = _startCase(HOME_OWNER)
  const TRADE_TAB = _startCase(TRADE_ACCOUNT)
  useEffect(() => {
    if (activeTab === HOME_OWNER_TAB) {
      setHomeOwnerData(userData)
    } else {
      setProfessionalData(userData)
    }
  }, [userData])

  const [professional, setProfessional] = useState({
    active: true,
    company: "",
    professionalName: "",
  })
  const [requestMessage, setRequestMessage] = useState("")
  const [imageFiles, setImageFiles] = useState([])
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState("")
  const [count, setCount] = useState(0)
  const [selectedSupport, setSelectedSupport] = useState("")

  const {
    i18n = {},
    submitSuccess = false,
    setActiveTab = () => {},
    activeTab = "",
    sourceKey = "",
    handleSubmitMsg = () => {},
    showToaster = () => {},
    authorData = {},
    legalPages = {},
    continueShoppingUrl = "",
  } = props

  const contactInfoLabels = {
    zipCodeLabel: authorData?.zipCodeLabel,
    stateLabel: authorData?.stateLabel,
  }
  const validateExist = async field => {
    switch (field) {
      case "fname":
      case "lname":
        if (
          !userData[field]?.trim() ||
          !(await validateField("name", userData[field]))
        ) {
          return field === "fname" ? i18n?.fnameError : i18n?.lnameError
        }
        break
      case "email":
        if (!userData[field]) return i18n?.noEmailError
        else if (!(await validateField("email", userData[field])))
          return i18n?.validEmail
        break
      case "business":
        if (!userData?.company) return i18n?.noCompanyError
        break
      case "cemail":
        if (!userData[field]) return i18n?.noEmailError
        else if (!(await validateField("email", userData[field])))
          return i18n?.validEmail
        else if (userData?.email !== userData?.cemail)
          return i18n?.emailMatchError
        break
      case "phone":
        if (!userData?.phone) return i18n?.noPhoneError
        else if (!validatePhone(userData?.phone)) return i18n?.validPhone
        break
      case "zipcode":
        if (!userData.zipcode.trim()) return i18n.zipcodeError
        else if (userData.zipcode.length < 5) return i18n.validZipcode
        break
      case "company":
        if (!professional?.company) return i18n?.noCompanyError
        break
      case "professionalName":
        if (!professional?.professionalName) return i18n?.contactNameError
        break
      default:
        return
    }
  }

  const onBlurError = async field => {
    setError({
      ...error,
      [field]: await validateExist(field),
    })
  }

  const radioKeyDown = e => {
    const { target: radioTarget = {}, key: radioKey = "" } = e
    const { nextSibling, previousSibling } = radioTarget
    const nextRadio = nextSibling
    const previousRadio = previousSibling
    if (radioKey === "ArrowLeft" && previousRadio) {
      previousRadio?.focus()
    } else if (radioKey === "ArrowRight" && nextRadio) {
      nextRadio?.focus()
    } else if (radioKey === "Enter" || radioKey === " ") {
      e?.preventDefault()
      radioTarget?.click()
    }
  }

  const getErrorList = async () => {
    const errorList = {
      fname: await validateExist("fname"),
      lname: await validateExist("lname"),
      email: await validateExist("email"),
      cemail: await validateExist("cemail"),
      zipcode: await validateExist("zipcode"),
      phone: await validateExist("phone"),
      helpTopic: await validateExist("helpTopic"),
    }

    if (activeTab === TRADE_TAB) {
      errorList.business = await validateExist("business")
    } else if(activeTab === HOME_OWNER_TAB && professional?.active) {
      errorList.professionalName = await validateExist("professionalName")
      errorList.company = await validateExist("company")
    }
    return errorList
  }

  const onSubmit = async () => {
    setLoading(true)
    const errorDataList = []
    const errorList = await getErrorList()
    const imageLinks = imageFiles?.map(image => ({
      link: image?.link,
      comment: image?.comment,
    }))
    setError(errorList)

    if (errorList?.fname) {
      errorDataList.push(`contact-user-fname`)
    } else if (errorList?.lname) {
      errorDataList.push(`contact-user-lname`)
    } else if (errorList?.email) {
      errorDataList.push(`contact-user-email`)
    } else if (errorList?.cemail) {
      errorDataList.push(`contact-user-cemail`)
    } else if (errorList?.zipcode) {
      errorDataList.push(`contact-user-zipcode`)
    } else if (errorList?.phone) {
      errorDataList.push(`contact-user-phone`)
    } else if (errorList?.helpTopic) {
      errorDataList.push(`type-of-support`)
    }
    if (activeTab === HOME_OWNER_TAB && professional?.active) {
      if (errorList?.professionalName) {
        errorDataList.push(`kf-professional-name`)
      } else if (errorList?.company) {
        errorDataList.push(`contact-user-company`)
      }
    }
    if (activeTab === TRADE_TAB) {
      if (errorList?.business) {
        errorDataList.push(`contact-user-business`)
      }
    }
    errorDataList.forEach((item, i) => {
      if (i === 0) document.getElementById(`${item}`)?.focus()
    })

    const isInvalid = Object.values(errorList)?.find(el => el)
    if (!isInvalid) {
      const { fname, lname, email, zipcode, country, phone, company, market } =
        userData

      const body = {
        FirstName: fname,
        LastName: lname,
        Email: email,
        PostalCode: zipcode,
        Country: country?.code,
        Phone: phone,
        AttachmentLinks: imageLinks,
        CustomerType: activeTab,
        InteractionType: "ContactUs",
        DescribeYourRequest: requestMessage,
        TopicSelected: selectedSupport,
        SourceKey: [
          {
            Type: sourceKey,
            Value: `${Math.floor(1000 + Math.random() * 9000)}`,
            CreateDate: new Date().toISOString(),
            UpdateDate: new Date().toISOString(),
          },
        ],
        Website: sourceKey,
      }

      if (activeTab === TRADE_TAB) {
        body["Company"] = company
        body["Market"] = market
      }

      if (activeTab === HOME_OWNER_TAB && professional?.active) {
        body["IsProfessional"] = professional?.active
        if (professional?.active) {
          body["OtherProfessionalCompany"] = professional?.company
          body["ProfessionalName"] = professional?.professionalName
        }
      }

      sanitizeObject(body)

      validateRecaptcha().then(res => {
        if (res?.success) {
          apim
            .post(CONTACT_US_VALIDATE_RECAPTCHA_ENDPOINT, body)
            .then(() => {
              handleSubmitMsg(true)
              addFormAnalyticsData(
                activeTab,
                submitSuccess,
                "",
                true,
                body?.TopicSelected,
                body?.IsProfessional,
                body?.Market
              )
              setTimeout(() => {
                setActiveTab(HOME_OWNER_TAB)
              }, 0)
            })
            .catch(err => {
              setFormError(i18n?.serverError)
              addFormAnalyticsData(
                activeTab,
                submitSuccess,
                i18n?.serverError,
                false,
                body?.TopicSelected,
                body?.IsProfessional,
                body?.Market
              )
              setLoading(false)
              // eslint-disable-next-line no-console
              console.log(err)
            })
        } else {
          setLoading(false)
          addFormAnalyticsData(
            activeTab,
            submitSuccess,
            i18n?.genError,
            false,
            body?.TopicSelected,
            body?.IsProfessional,
            body?.Market
          )
          showToaster(i18n?.genError)
        }
      })
    } else {
      setLoading(false)
      const allErrors = []
      for (const key in errorList) {
        if (errorList[key]) {
          allErrors?.push(errorList[key]?.toLowerCase())
        }
      }
      addFormAnalyticsData(
        activeTab,
        submitSuccess,
        allErrors?.join("|"),
        false,
        selectedSupport,
        activeTab === HOME_OWNER_TAB ? professional?.active : null,
        activeTab === TRADE_TAB ? userData?.market : null
      )
    }
  }

  const checkDisabled = userdata => {
    if (
      userdata?.fname?.trim() &&
      userdata?.lname?.trim() &&
      userdata?.email?.trim() &&
      userdata?.country &&
      userdata?.phone &&
      selectedSupport
    )
      return false
    return true
  }

  const changeSupport = support => {
    setSelectedSupport(support)
  }

  return (
    <div>
      {!submitSuccess ? (
        <div className="kf-react-container">
          <div className="contact-us-main">
            <div className="title">
              <h2 className="question">{i18n?.contactInformation}</h2>
              <span className="required-fields">{i18n?.requiredInfoText}</span>
            </div>
            <FormTabs
              label={[HOME_OWNER_TAB, TRADE_TAB]}
              customClass="gbh-data-layer"
              analyticsData={`{"eventAction":"support:contact:form:{?}","eventName":"support:contact:form:{?}","eventType":"navigation","eventMsg":"n/a","eventStatus":"n/a","internalLinkName":"{?}","internalLinkPosition":"contact us","internalLinkType":"support:contact:contact information","internalLinkZoneName":"support:contact:form","internalLinkURL":"n/a","clickInternalLinks":"true"}`}
              activeTab={activeTab}
              changeActive={l => {
                setActiveTab(l)
                setUserData(
                  l === HOME_OWNER_TAB ? homeOwnerData : professionalData
                )
                setError({})
              }}
            />
            <ContactInfo
              key={activeTab}
              activeTab={activeTab}
              data={userData}
              labels={contactInfoLabels}
              setData={setUserData}
              error={error}
              i18n={i18n}
              onBlur={onBlurError}
              setError={setError}
            />
            {activeTab === HOME_OWNER_TAB && (
              <WithProfessional
                i18n={i18n}
                data={professional}
                setData={setProfessional}
                radioKeyDown={radioKeyDown}
                error={error}
                onBlur={onBlurError}
              />
            )}
            <div className="optional-form">
              <div id="contactus-request-message" className="question">
                {i18n.selectTypeOfSupport}
              </div>
              <Dropdown
                id="type-of-support"
                data={props?.helpTopic?.map(topic => topic)}
                name="selectedSupport"
                value={selectedSupport ?? ""}
                label={`${i18n.howCanWeHelp}*`}
                onChange={changeSupport}
                onBlur={onBlurError}
                showError={error.selectedSupport}
                errorMessage={error.selectedSupport}
              />
              <div id="contactus-enquiry-text" className="question">
                {i18n.requestDetailHeading}{" "}
              </div>
              <p className="description">{i18n?.requestDetail}</p>
              <div className={styles.wrapper}>
                <div className="form-wrapper textarea__wrapper">
                  <textarea
                    className="textarea-message"
                    id="requestMessage"
                    aria-labelledby="contactus-request-message"
                    value={requestMessage}
                    placeholder="Message"
                    onChange={async e => {
                      const { value } = e.target
                      if (
                        (await validateField("contactusMessage", value)) ||
                        value === ""
                      ) {
                        setCount(value?.length)
                        setRequestMessage(value)
                      }
                    }}
                    maxLength="500"
                  />
                  <label aria-hidden="true" className="textare-label-message">
                    {i18n?.messageLabel}
                  </label>
                  {count !== 0 && (
                    <span className="contact-us-count">{count}/500</span>
                  )}
                </div>
              </div>
            </div>
            <div className="optional-form" id="contactus-upload-wrapper">
              <div id="contactus-upload-text" className="question">
                {i18n?.uploadPhoto}
              </div>
              <p className="description">{i18n?.uploadCountLimit}</p>
              <p className="upload-notes">
                <span>{i18n?.uploadFileTypes}</span>
                <span>{i18n?.uploadFileExtn}</span>
              </p>
              <p className="upload-notes">
                <span>{i18n?.uploadSizeLimit}</span>
                <span>{i18n?.uploadMBLimit}</span>
              </p>
              <UploadImage
                imageList={imageFiles}
                updateList={setImageFiles}
                i18n={i18n}
                activeTab={activeTab}
                buttonAnalyticsData={getAnalyticsData(
                  activeTab,
                  formError,
                  submitSuccess,
                  false
                )}
              />
            </div>
            <div className="contact-us-footer">
              {formError && (
                <p className="contact-us-footer__error-msg">{formError}</p>
              )}
              <Button
                role="button"
                id="submit-contact-form-button"
                disabled={checkDisabled(userData)}
                onClick={onSubmit}
                type="black"
                label={i18n?.submitButton}
                flexible
                loading={loading}
                useDefaultClass={true}
              />
              <ReCaptcha getAnalyticsData={getRecaptchaAnalytics} />
              <div className="contact-us__policies">
                <a
                  href={legalPages?.privacyPolicy}
                  data-gbh-data-layer={getFooterEventInfo(
                    "privacy policy",
                    legalPages?.privacyPolicy
                  )}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => getSiteWideModal(e, legalPages?.privacyPolicy)}
                >
                  {i18n?.privacyPolicy}
                </a>
                <a
                  href={legalPages?.subjectAccessRequest}
                  data-gbh-data-layer={getFooterEventInfo(
                    "subject access request",
                    legalPages?.subjectAccessRequest,
                    "false"
                  )}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e =>
                    getSiteWideModal(e, legalPages?.subjectAccessRequest)
                  }
                >
                  {i18n?.subjectAccessRequest}
                </a>
                <a
                  href={legalPages?.termsOfService}
                  data-gbh-data-layer={getFooterEventInfo(
                    "terms of service",
                    legalPages?.termsOfService
                  )}
                  target="_blank"
                  rel="noreferrer"
                  onClick={e => getSiteWideModal(e, legalPages?.termsOfService)}
                >
                  {i18n?.termsOfService}
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="contact-us__success-message-wrapper">
          <ContactUsConfirmation
            i18n={i18n}
            setSubmitSuccess={handleSubmitMsg}
            continueShoppingUrl={continueShoppingUrl}
          />
        </div>
      )}
    </div>
  )
}

export default withOrientationChange(ContactUs)
